import { DetailsHeader } from '@/components/details';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { DetailsSearch } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import { bookingTabLinks } from '@/features/dashboard/bookingChanges/routes/detailsTabs.ts';
import { BookingBillVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { ChangeDraftVm } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import { getBookingStatusLabel } from '@/features/dashboard/bookingChanges/types/BillViewModel.utils.ts';
import {
  ChangeEditAvailabilityVm,
  EditModeToggle,
} from '@/features/dashboard/bookingChanges/components/editing';
import BookingActionsMenu from '@/features/dashboard/bookingChanges/components/bookingActions/BookingActionsMenu.tsx';
import BookingDetailsBanner from './BookingDetailsBanner';
import { bookingStatusTone } from './bookingHeaderPresentation';
import { participantCountLabel } from './bookingHeaderPresentation';

interface BookingDetailsHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  bookingId: string;
  /** Adres ekranu przekazywany zakładkom — patrz komentarz przy `tabs`. */
  search: DetailsSearch;
  /** Rachunek dojeżdża po pierwszym rysowaniu, stąd `null`. */
  view: BookingBillVm | null;
  isEditing: boolean;
  draft: ChangeDraftVm | null;
  editAvailability: ChangeEditAvailabilityVm;
  onToggleEditing: (nextEditing: boolean) => void;
  onWithdrawPackage?: () => void;
  isWithdrawing?: boolean;
  /** Otwiera okno rezygnacji uczestnika; brak = akcji nie pokazujemy. */
  onOpenWithdrawal?: () => void;
  onlyChanged: boolean;
  onOnlyChangedChange: (onlyChanged: boolean) => void;
}

/**
 * Nagłówek ekranu rezerwacji: tożsamość rezerwacji, przełącznik trybu edycji,
 * zakładki i belka otwartego szkicu.
 *
 * Świadomie nie dostaje `onOpenFullPackage` („Cała paczka ↗”). Ekrany osób mają
 * ten przycisk, bo prowadzi z uczestnika lub zapisującego na poziom całej
 * rezerwacji — a tutaj operator już na nim stoi, więc przycisk prowadziłby na
 * ten sam ekran. W prototypie widać go na C2 wyłącznie dlatego, że wszystkie
 * trzy ekrany dzielą jeden nagłówek.
 */
const BookingDetailsHeader = ({
  bookingId,
  search,
  view,
  isEditing,
  draft,
  editAvailability,
  onToggleEditing,
  onWithdrawPackage,
  isWithdrawing,
  onOpenWithdrawal,
  onlyChanged,
  onOnlyChangedChange,
  ...props
}: BookingDetailsHeaderProps) => {
  return (
    <DetailsHeader
      initials="RE"
      avatarTone="dark"
      title={view?.bookingCode ?? ''}
      badges={
        view
          ? [
              <StatusDot
                className="text-[15px]"
                tone={bookingStatusTone[view.status]}
                label={getBookingStatusLabel(view.status)}
              />,
            ]
          : undefined
      }
      meta={
        view
          ? [
              `${view.tourName} · ${participantCountLabel(view.participants)}`,
              {
                label: `Płatnik ${view.client.person.fullName}`,
                to: '/app/clients/$clientId/info' as const,
                params: { clientId: view.client.person.id },
              },
            ]
          : []
      }
      tabs={bookingTabLinks(bookingId, search)}
      // Kafle menu mobilnego mieszkają pod gołym adresem rezerwacji; `search`
      // niesie tryb edycji, żeby powrót do menu go nie zgubił.
      menuLink={{
        to: '/app/bookings/$bookingId',
        params: { bookingId },
        search,
      }}
      highlighted={isEditing}
      actions={
        <div className="flex items-center gap-3">
          {view?.status !== BookingStatus.CANCELLED && (
            <EditModeToggle
              isEditing={isEditing}
              availability={editAvailability}
              onToggle={onToggleEditing}
            />
          )}
          {/* Rezygnacja wymaga rezerwacji bez otwartej paczki, więc w trybie
              edycji menu nie ma — backend i tak by ją odrzucił. */}
          {view && !isEditing && (
            <BookingActionsMenu
              clientId={view.clientId}
              onOpenWithdrawal={onOpenWithdrawal}
            />
          )}
        </div>
      }
      banner={
        draft && (
          <BookingDetailsBanner
            draft={draft}
            isEditing={isEditing}
            editLock={editAvailability}
            onResumeEditing={() => onToggleEditing(true)}
            onWithdrawPackage={onWithdrawPackage}
            isWithdrawing={isWithdrawing}
            onlyChanged={onlyChanged}
            onOnlyChangedChange={onOnlyChangedChange}
          />
        )
      }
      {...props}
    />
  );
};

export default BookingDetailsHeader;
